import type {
  LocationCreateRequest,
  LocationResponse,
  LocationUpdateRequest,
  MembershipRole,
} from "@safeplate/contracts";

import { requestApi } from "../api/client.js";
import type { SetupField, SetupItem, SetupResource } from "./resources.js";

type Page<T> = { items: T[]; next_cursor: string | null };

function withLocation(path: string, locationId: string): string {
  return `${path}?location_id=${encodeURIComponent(locationId)}`;
}

function listPath(resource: SetupResource, locationId: string): string {
  if (resource.key === "areas") return `/locations/${locationId}/areas`;
  if (
    ["assets", "cleaning", "water", "risks", "separation", "methods"].includes(
      resource.key,
    )
  ) {
    return withLocation(`/${resource.path}`, locationId);
  }
  if (resource.key === "reminders") return "/renewal-reminders?status=active";
  return `/${resource.path}?limit=200`;
}

export async function listSetupItems(
  resource: SetupResource,
  locationId: string,
): Promise<SetupItem[]> {
  const response = await requestApi<SetupItem[] | Page<SetupItem>>(
    listPath(resource, locationId),
  );
  let items = Array.isArray(response) ? response : response.items;
  if (resource.key === "suppliers") {
    items = items.filter(
      (item) =>
        Array.isArray(item.locations) &&
        item.locations.some(
          (location) =>
            typeof location === "object" &&
            location !== null &&
            "location_id" in location &&
            location.location_id === locationId,
        ),
    );
  }
  if (resource.key === "documents" || resource.key === "reminders") {
    items = items.filter(
      (item) => item.location_id === locationId || item.location_id === null,
    );
  }
  return items;
}

function nullable(value: FormDataEntryValue | null): string | undefined {
  const text = typeof value === "string" ? value.trim() : "";
  return text.length > 0 ? text : undefined;
}

export function formPayload(
  fields: readonly SetupField[],
  form: FormData,
  current?: SetupItem,
): Record<string, boolean | number | string | null> {
  const body: Record<string, boolean | number | string | null> = {};
  for (const field of fields) {
    if (field.type === "checkbox") {
      body[field.key] = form.get(field.key) === "on";
      continue;
    }
    const value = nullable(form.get(field.key));
    if (value === undefined) {
      if (current !== undefined && !field.required) body[field.key] = null;
      continue;
    }
    body[field.key] = field.type === "number" ? Number(value) : value;
  }
  return body;
}

function payloadForResource(
  resource: SetupResource,
  locationId: string,
  values: Record<string, boolean | number | string | null>,
  current?: SetupItem,
): Record<string, unknown> {
  if (resource.key === "areas") return values;
  if (resource.key === "suppliers") {
    const { goods_summary: goodsSummary, ...supplier } = values;
    const priorLocations = Array.isArray(current?.locations)
      ? current.locations
      : [];
    const locations = priorLocations
      .filter(
        (location): location is Record<string, unknown> =>
          typeof location === "object" && location !== null,
      )
      .map((location) => ({
        approved_from: location.approved_from,
        delivery_days: location.delivery_days,
        goods_summary: location.goods_summary,
        location_id: location.location_id,
        order_days: location.order_days,
      }))
      .filter((location) => location.location_id !== locationId);
    locations.push({
      approved_from: new Date().toISOString().slice(0, 10),
      delivery_days: [],
      goods_summary: goodsSummary ?? null,
      location_id: locationId,
      order_days: [],
    });
    return { ...supplier, locations };
  }
  if (resource.key === "documents") {
    return { ...values, location_id: locationId };
  }
  if (resource.key === "reminders") {
    return {
      ...values,
      location_id: locationId,
      remind_offsets_days: [60, 30, 7],
    };
  }
  return { ...values, location_id: locationId };
}

export async function saveSetupItem(
  resource: SetupResource,
  locationId: string,
  values: Record<string, boolean | number | string | null>,
  current?: SetupItem,
): Promise<SetupItem> {
  const path =
    current === undefined
      ? resource.key === "areas"
        ? `/locations/${locationId}/areas`
        : `/${resource.path}`
      : `/${resource.path}/${current.id}`;
  return requestApi<SetupItem>(path, {
    body: JSON.stringify(
      payloadForResource(resource, locationId, values, current),
    ),
    method: current === undefined ? "POST" : "PATCH",
  });
}

export async function archiveSetupItem(
  resource: SetupResource,
  item: SetupItem,
): Promise<void> {
  if (resource.key === "reminders") {
    await requestApi(`/${resource.path}/${item.id}`, {
      body: JSON.stringify({ status: "dismissed" }),
      method: "PATCH",
    });
    return;
  }
  await requestApi(`/${resource.path}/${item.id}`, { method: "DELETE" });
}

export interface QrIssueResponse {
  asset_id: string;
  printable_label: { format: "link"; label_url: string };
  qr_url: string;
  token: string;
}

export interface AssetLabelSource {
  id: string;
  location_id: string;
  name: string;
  qr_url: string | null;
}

// The organisation is named explicitly: a page opened on its own (such as a
// printable label) may ask before the shell has set it.
export function getAsset(
  assetId: string,
  orgId: string,
): Promise<AssetLabelSource> {
  return requestApi<AssetLabelSource>(`/assets/${assetId}`, {
    headers: { "X-Org-Id": orgId },
  });
}

export function issueAssetQr(assetId: string): Promise<QrIssueResponse> {
  return requestApi<QrIssueResponse>(`/assets/${assetId}/qr`, {
    method: "POST",
  });
}

export function createLocation(
  input: LocationCreateRequest,
  options: { idempotencyKey?: string; orgId?: string } = {},
): Promise<LocationResponse> {
  return requestApi<LocationResponse>("/locations", {
    body: JSON.stringify(input),
    headers: {
      "Idempotency-Key": options.idempotencyKey ?? crypto.randomUUID(),
      ...(options.orgId ? { "X-Org-Id": options.orgId } : {}),
    },
    method: "POST",
  });
}

export function updateLocation(
  id: string,
  input: LocationUpdateRequest,
): Promise<LocationResponse> {
  return requestApi<LocationResponse>(`/locations/${id}`, {
    body: JSON.stringify(input),
    method: "PATCH",
  });
}

export interface StaffProfile extends SetupItem {
  display_name: string;
  email: string | null;
  job_title: string | null;
  location_ids: string[];
  quick_pin_enabled: boolean;
  status: "active" | "inactive";
  user_id: string | null;
  membership?: Membership;
  invitation?: StaffInvitation | null;
}

export interface StaffInvitation {
  email: string;
  expires_at: string;
  id: string;
  role: MembershipRole;
  status: "pending" | "expired";
}

export interface Membership {
  created_at: string;
  granted_by: string | null;
  id: string;
  location_id: string | null;
  role: MembershipRole;
  status: "active" | "revoked";
  updated_at: string;
  user_id: string;
}

export interface Invitation {
  accepted_at: string | null;
  created_at: string;
  email: string;
  expires_at: string;
  id: string;
  location_ids: string[];
  revoked_at?: string | null;
  role: MembershipRole;
  staff_profile_id: string;
}

export async function listStaff(locationId: string): Promise<StaffProfile[]> {
  const page = await requestApi<Page<StaffProfile>>(
    `/staff?location_id=${encodeURIComponent(locationId)}&limit=200`,
  );
  return page.items;
}

export function createStaff(
  input: Pick<StaffProfile, "display_name" | "email" | "job_title"> & {
    location_ids: string[];
    quick_pin?: string;
  },
): Promise<StaffProfile> {
  return requestApi<StaffProfile>("/staff", {
    body: JSON.stringify(input),
    method: "POST",
  });
}

// Founder decision, 24 Oct 2026: a manager creates each person's login
// (email, a password they choose and the PIN), and it works at once
// (RDY-BE-22). Off sends invitations only, as before.
export const STAFF_LOGINS_READY = true;

export interface StaffLoginInput {
  display_name: string;
  email: string;
  location_ids: string[];
  password: string;
  quick_pin?: string;
  role: MembershipRole;
}

// Up to 20 people, all or nothing. An error names the person in its field
// ("people.<i>.email").
export function createStaffLogins(
  people: readonly StaffLoginInput[],
): Promise<{ people: StaffProfile[] }> {
  return requestApi<{ people: StaffProfile[] }>("/staff/accounts", {
    body: JSON.stringify({ people }),
    headers: { "Idempotency-Key": crypto.randomUUID() },
    method: "POST",
  });
}

export function setStaffQuickPin(
  id: string,
  pin: string,
): Promise<{ enabled: boolean; updated_at: string }> {
  return requestApi(`/staff/${id}/quick-pin`, {
    body: JSON.stringify({ pin }),
    method: "PUT",
  });
}

export function updateStaff(
  id: string,
  input: Partial<
    Pick<
      StaffProfile,
      "display_name" | "email" | "job_title" | "location_ids" | "status"
    >
  >,
): Promise<StaffProfile> {
  return requestApi<StaffProfile>(`/staff/${id}`, {
    body: JSON.stringify(input),
    method: "PATCH",
  });
}

export function grantMembership(input: {
  location_id: string | null;
  role: MembershipRole;
  user_id: string;
}): Promise<Membership> {
  return requestApi<Membership>("/memberships", {
    body: JSON.stringify(input),
    method: "POST",
  });
}

export function changeMembership(
  id: string,
  input: { location_id: string | null; role: MembershipRole },
): Promise<Membership> {
  return requestApi<Membership>(`/memberships/${id}`, {
    body: JSON.stringify(input),
    method: "PATCH",
  });
}

export function inviteStaff(input: {
  email: string;
  location_ids: string[];
  role: MembershipRole;
  staff_profile_id: string;
}): Promise<Invitation> {
  return requestApi<Invitation>("/invitations", {
    body: JSON.stringify(input),
    headers: { "Idempotency-Key": crypto.randomUUID() },
    method: "POST",
  });
}

export function reissueInvitation(id: string): Promise<Invitation> {
  return requestApi<Invitation>(`/invitations/${id}/reissue`, {
    headers: { "Idempotency-Key": crypto.randomUUID() },
    method: "POST",
  });
}

export function revokeInvitation(id: string): Promise<Invitation> {
  return requestApi<Invitation>(`/invitations/${id}/revoke`, {
    method: "POST",
  });
}
